import { dateToJD } from '../astronomy/julian-day';
import { equationOfTime } from '../astronomy/solar-longitude';

/**
 * 그레고리력 윤년 여부를 확인한다.
 */
export function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

/**
 * 지정 연월의 일수를 반환한다.
 */
export function daysInMonth(year: number, month: number): number {
  const days = [31, isLeapYear(year) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  return days[month - 1];
}

/**
 * 날짜에 일수를 더한다. { year, month, day }를 반환한다.
 */
export function addDays(year: number, month: number, day: number, days: number): { year: number; month: number; day: number } {
  let d = day + days;
  let m = month;
  let y = year;

  if (days >= 0) {
    while (d > daysInMonth(y, m)) {
      d -= daysInMonth(y, m);
      m++;
      if (m > 12) {
        m = 1;
        y++;
      }
    }
  } else {
    while (d < 1) {
      m--;
      if (m < 1) {
        m = 12;
        y--;
      }
      d += daysInMonth(y, m);
    }
  }

  return { year: y, month: m, day: d };
}

/**
 * 양력 날짜가 지원 범위(1900-2100) 내인지 검증한다.
 */
export function validateSolarDate(year: number, month: number, day: number): void {
  if (year < 1900 || year > 2100) {
    throw new Error(`Year ${year} is out of supported range (1900-2100)`);
  }
  if (month < 1 || month > 12) {
    throw new Error(`Month ${month} is invalid (must be 1-12)`);
  }
  const maxDay = daysInMonth(year, month);
  if (day < 1 || day > maxDay) {
    throw new Error(`Day ${day} is invalid for ${year}-${month} (must be 1-${maxDay})`);
  }
}

/**
 * 진태양시 보정을 적용한다.
 *
 * 진태양시 = 표준시 + 경도 보정 + 균시차
 *
 * - 경도 보정: (출생지 경도 - 기준 자오선 135°E) × 4분
 * - 균시차: 지구 궤도 이심률과 자전축 기울기로 인해 발생, 연중 -14~+16분 변동
 *
 * @param year - 출생 연도 (균시차 계산용)
 * @param month - 출생 월
 * @param day - 출생 일
 * @param hour - 출생 시
 * @param minute - 출생 분
 * @param longitude - 출생지 경도 (기본값: 126.978, 서울)
 * @returns 보정된 { hour, minute, dayOffset }
 */
export function applyTrueSolarTimeCorrection(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  longitude: number = 126.978
): { hour: number; minute: number; dayOffset: number; longitudeCorrection: number; equationOfTime: number } {
  // 1. Longitude correction: (longitude - standardMeridian) × 4 minutes
  const longitudeCorrection = (longitude - 135.0) * 4;

  // 2. Equation of Time (균시차)
  const jd = dateToJD(year, month, day, hour, minute, 0);
  const eot = equationOfTime(jd);

  // Total correction = longitude correction + equation of time
  let totalMinutes = hour * 60 + minute + longitudeCorrection + eot;

  let dayOffset = 0;
  if (totalMinutes < 0) {
    dayOffset = -1;
    totalMinutes += 24 * 60;
  } else if (totalMinutes >= 24 * 60) {
    dayOffset = 1;
    totalMinutes -= 24 * 60;
  }

  return {
    hour: Math.floor(totalMinutes / 60),
    minute: Math.floor(totalMinutes % 60),
    dayOffset,
    longitudeCorrection,
    equationOfTime: eot,
  };
}
